import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AuthService } from '../../../../core/auth/auth.service';

function passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value as string;
  if (!value) return null;
  if (value.length < 8) return { minLength: true };
  if (!/[A-Z]/.test(value)) return { noUppercase: true };
  if (!/\d/.test(value)) return { noNumber: true };
  return null;
}

type ConfirmState = 'form' | 'loading' | 'token_expired' | 'invalid_token';

@Component({
  selector: 'app-reset-password-confirm',
  imports: [
    RouterLink,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './confirm.html',
  styleUrl: './confirm.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordConfirmComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  private token = '';

  protected readonly pageState = signal<ConfirmState>('form');
  protected readonly showPassword = signal(false);

  protected readonly form = this.fb.group({
    newPassword: ['', [Validators.required, passwordStrengthValidator]],
  });

  protected get newPasswordControl() {
    return this.form.controls.newPassword;
  }

  ngOnInit(): void {
    const token = this.route.snapshot.queryParamMap.get('token');
    if (!token) {
      this.pageState.set('invalid_token');
      return;
    }
    this.token = token;
  }

  protected togglePasswordVisibility(): void {
    this.showPassword.update((v) => !v);
  }

  protected onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.pageState() === 'loading' || this.form.invalid) return;

    this.pageState.set('loading');

    this.authService.confirmPasswordReset(this.token, this.newPasswordControl.value!).subscribe({
      next: () => void this.router.navigate(['/dashboard']),
      error: (err: unknown) => {
        if (err instanceof HttpErrorResponse) {
          const authErr = this.authService.getAuthError(err);
          this.pageState.set(authErr?.code === 'TOKEN_EXPIRED' ? 'token_expired' : 'invalid_token');
        } else {
          this.pageState.set('invalid_token');
        }
        this.cdr.markForCheck();
      },
    });
  }
}
